import { decryptJson, encryptJson, PassphraseSession, randomSalt, type Bytes } from '@/lib/crypto';
import type { SourceAuth } from '@/sources/types';
import { getDb, type CredentialRow } from './schema';

/**
 * Encrypted credential CRUD. Callers never touch `ciphertext`/`iv` directly —
 * `getAuth` returns the decrypted {@link SourceAuth} blob, `setAuth` encrypts
 * and persists.
 *
 * Every operation throws if the passphrase isn't unlocked. UI should prompt
 * via {@link PassphraseSession} before invoking these.
 */

const KDF_SALT_KEY = 'kdf.salt';

function asBytes(input: Uint8Array | number[]): Bytes {
  const arr = Array.isArray(input) ? input : Array.from(input);
  const out = new Uint8Array(new ArrayBuffer(arr.length));
  for (let i = 0; i < arr.length; i++) out[i] = arr[i] ?? 0;
  return out;
}

/**
 * Ensure a salt exists in `meta`. Called by the passphrase-prompt component
 * before deriving a key. Returns the existing or newly generated salt.
 */
export async function getOrCreateKdfSalt(): Promise<Bytes> {
  const db = await getDb();
  const row = await db.get('meta', KDF_SALT_KEY);
  if (row && row.value instanceof Uint8Array) {
    return asBytes(row.value);
  }
  if (row && Array.isArray(row.value)) {
    return asBytes(row.value as number[]);
  }
  const salt = randomSalt();
  await db.put('meta', { key: KDF_SALT_KEY, value: salt });
  return salt;
}

export async function listCredentialRows(): Promise<CredentialRow[]> {
  const db = await getDb();
  return db.getAll('credentials');
}

export async function getCredentialRow(source: string): Promise<CredentialRow | undefined> {
  const db = await getDb();
  return db.get('credentials', source);
}

export async function getAuth(source: string): Promise<SourceAuth | undefined> {
  const row = await getCredentialRow(source);
  if (!row) return undefined;
  const key = PassphraseSession.requireKey();
  return decryptJson<SourceAuth>(row.ciphertext, row.iv, key);
}

type SetAuthOptions = {
  status?: CredentialRow['status'];
  username?: string;
};

async function setAuthWithEncryptionKey(
  source: string,
  auth: SourceAuth,
  key: CryptoKey,
  opts: SetAuthOptions,
  canWrite?: () => boolean,
): Promise<boolean> {
  const { ct, iv } = await encryptJson(auth, key);
  const db = await getDb();
  const row: CredentialRow = {
    source,
    ciphertext: ct,
    iv,
    addedAt: Date.now(),
    status: opts.status ?? 'ok',
  };
  if (opts.username !== undefined) row.username = opts.username;
  if (auth.anonymous) row.mode = 'anonymous';
  if (canWrite && !canWrite()) return false;
  await db.put('credentials', row);
  return true;
}

export async function setAuth(
  source: string,
  auth: SourceAuth,
  opts: SetAuthOptions = {},
): Promise<void> {
  await setAuthWithEncryptionKey(source, auth, PassphraseSession.requireKey(), opts);
}

export async function setAuthWithKey(
  source: string,
  auth: SourceAuth,
  key: CryptoKey,
  opts: SetAuthOptions = {},
  canWrite?: () => boolean,
): Promise<boolean> {
  return setAuthWithEncryptionKey(source, auth, key, opts, canWrite);
}

export async function markCredentialStatus(
  source: string,
  status: CredentialRow['status'],
): Promise<void> {
  const db = await getDb();
  const row = await db.get('credentials', source);
  if (!row) return;
  row.status = status;
  await db.put('credentials', row);
}

export async function deleteCredential(source: string): Promise<void> {
  const db = await getDb();
  await db.delete('credentials', source);
}
